/**
 * Bank dossier — the Legado (B3 · Gestão de Garantias) information set that
 * every work order carries for the requesting bank.
 *
 * The front renders it from `WorkOrderData.bank_dossier`. For regular work
 * orders it is served by the avaliahub-api (`BankDossierResponse` below),
 * apart from the control center that serves the rest of the OS.
 * Identifiers are English; each doc-comment keeps the legacy label (PT) the
 * bank teams know. Values the bank never filled arrive as `null`.
 *
 * Dates are ISO strings (UTC, with offset) like the rest of the work order.
 * Money is a number in BRL. `ltv` is a percentage (0–100).
 */
import { Status } from '../../enumerations';

/** The 17 legacy milestones ("DATAS" block), in the exact order of the B3 screen. */
export type BankMilestoneKey =
  | 'report_delivery_date'
  | 'bank_due_date'
  | 'created_at'
  | 'appraisal_company_due_date'
  | 'assignment_date'
  | 'engineer_assignment_date'
  | 'acceptance_date'
  | 'inspection_date'
  | 'probable_inspection_date'
  | 'report_completion_date'
  | 'report_received_at'
  | 'cancellation_date'
  | 'cancellation_request_date'
  | 'revision_request_date'
  | 'revision_closed_date'
  | 'rectification_request_date'
  | 'rectification_closed_date';

export const bankMilestoneKeys: BankMilestoneKey[] = [
  'report_delivery_date',
  'bank_due_date',
  'created_at',
  'appraisal_company_due_date',
  'assignment_date',
  'engineer_assignment_date',
  'acceptance_date',
  'inspection_date',
  'probable_inspection_date',
  'report_completion_date',
  'report_received_at',
  'cancellation_date',
  'cancellation_request_date',
  'revision_request_date',
  'revision_closed_date',
  'rectification_request_date',
  'rectification_closed_date',
];

export type BankMilestones = Record<BankMilestoneKey, string | null>;

export interface BankIdentification {
  /** Nº Controle Interno / Ordem de Serviço (identificador do banco). */
  bank_control_number: string | null;
  /** Nº da Pasta — agrupa todas as solicitações do mesmo caso/imóvel. */
  folder_number: string | null;
  /** Nº Solicitação. */
  request_number: string | null;
  /** 1 para "1ª Solicitação", 2 para "2ª Solicitação"… */
  request_ordinal: number | null;
  /** Sub-Status (Status Secundário) — texto livre do banco. */
  sub_status: string | null;
  /** Canal de entrada (agência, correspondente, digital…). */
  channel: string | null;
}

export interface BankFinancing {
  /** Tipo de Operação (aquisição, refinanciamento, portabilidade…). */
  operation_type: string | null;
  /** Tipo da Solicitação (isolado, lote, revisão, retificação…). */
  request_kind: string | null;
  /** Valor Compra e Venda. */
  purchase_sale_value: number | null;
  /** Valor Financiamento. */
  financing_value: number | null;
  /** LTV (%). */
  ltv: number | null;
  /** Valor Imóvel informado pelo banco, antes da avaliação. */
  property_value: number | null;
  /** Nº Proposta Original do Banco. */
  original_proposal_number: string | null;
  /** Nº Anterior da Proposta Original (histórico de renumeração). */
  previous_proposal_number: string | null;
  /** Proprietário do bem. */
  property_owner: string | null;
  /** Proponente / Comprador / Garantidor / Mutuário. */
  proponent_name: string | null;
  /** CPF / CNPJ do Proponente (digits only). */
  proponent_document: string | null;
  /** Uso de FGTS. */
  uses_fgts: boolean;
  /** Elegibilidade do Proponente. */
  proponent_eligibility: string | null;
  /** Propósito da Avaliação / Finalidade da Linha de crédito. */
  credit_line_purpose: string | null;
}

export interface BankAppraisalCompany {
  /** Engenheiro / Representante Legal Escalado. */
  escalated_engineer: string | null;
  /** Tipo de Revisão. */
  revision_type: string | null;
  /** Responsável pela Revisão. */
  revision_responsible: string | null;
  /** Informações Adicionais da credenciada. */
  additional_info: string | null;
}

export interface BankRequestingBank {
  /** Depto de Engenharia do Banco. */
  engineering_department: string | null;
  /** Analista da Área. */
  area_analyst: string | null;
  /** Nome do Gerente. */
  manager_name: string | null;
  /** Análise do Depto de Engenharia do Banco (passa por análise interna). */
  requires_bank_engineering_analysis: boolean;
}

export interface BankAddressExtras {
  /** Tipo de Logradouro separado (R, Av, Al…). */
  street_type: string | null;
  /** Andar. */
  floor: string | null;
  /** Nome do Condomínio / Empreendimento. */
  condominium_name: string | null;
  /** Endereço Original, como veio do banco, antes da normalização. */
  original_address: string | null;
  /** Área Nova (CEP inexistente)? */
  is_new_area: boolean;
}

export interface BankLinkedUnit {
  id: string;
  registry_number: string;
  quantity: number | null;
  unit_type: string | null;
  description: string | null;
}

export interface BankRegistryExtras {
  /** Nº Total de Unidades Vinculadas/Autônomas. */
  total_linked_units: number | null;
  /** Unidades vinculadas (empreendimentos com várias matrículas). */
  linked_units: BankLinkedUnit[];
}

export interface BankCharacteristicsExtras {
  /** Nº Blocos/Torres. */
  blocks_towers: number | null;
  /** Construção Nova. */
  is_new_construction: boolean;
  /** Imóvel Rural. */
  is_rural: boolean;
}

export interface BankReportKind {
  /** Laudo Original — referência ao laudo anterior quando a OS é revisão/retificação. */
  original_report_reference: string | null;
}

export interface BankCharge {
  id: string;
  /** Tipo de Honorário. */
  fee_type: string;
  /** Tipo de Cobrança. */
  charge_type: string | null;
  /** Valor Cobrado do Banco. */
  bank_charged_value: number | null;
  /** Honorário do Avaliador (repasse à credenciada). */
  appraiser_fee: number | null;
  /** Honorários banco/plataforma (take da plataforma). */
  platform_fee: number | null;
  created_at: string;
  /** Observações. */
  notes: string | null;
}

export interface BankCharges {
  items: BankCharge[];
  /** Total de Cobranças – Banco. */
  total_bank_charges: number;
  /** Total dos Honorários. */
  total_fees: number;
}

export interface BankContact {
  id: string;
  /** Tipo de Contato (proponente, proprietário, síndico, corretor, analista…). */
  contact_type: string;
  full_name: string;
  organization: string | null;
  /** Código de Contato. */
  contact_code: string | null;
  email: string | null;
  /** Telefone Comercial (digits only). */
  business_phone: string | null;
  /** Ramal. */
  extension: string | null;
  home_phone: string | null;
  mobile_phone: string | null;
  fax: string | null;
}

export interface BankFile {
  id: string;
  /** Tipo do Arquivo (Laudo, Matrícula, IPTU, Cálculos…). */
  file_type: string;
  file_name: string;
  size_bytes: number | null;
  created_at: string;
  /** Nº Controle Interno / OS de origem do arquivo. */
  bank_control_number: string | null;
  /** OS the file belongs to — differs from the current one when listing the folder. */
  work_order_id: number;
  work_order_reference: number;
  url: string | null;
  is_latest_report: boolean;
}

export interface BankRelatedRequest {
  /** Null when the sibling request lives outside the platform (not migrated). */
  work_order_id: number | null;
  reference_number: number | null;
  bank_control_number: string | null;
  request_ordinal: number;
  appraisal_company: string | null;
  report_type: string | null;
  status: Status | null;
  /** Free text used when `status` is unknown to the platform. */
  status_label: string | null;
  created_at: string;
}

export interface BankDossier {
  identification: BankIdentification;
  financing: BankFinancing;
  /** Valor Avaliação como dado de primeira classe (null até o laudo). */
  appraisal_value: number | null;
  milestones: BankMilestones;
  appraisal_company: BankAppraisalCompany;
  requesting_bank: BankRequestingBank;
  address_extras: BankAddressExtras;
  registry_extras: BankRegistryExtras;
  characteristics_extras: BankCharacteristicsExtras;
  report_kind: BankReportKind;
  charges: BankCharges;
  contacts: BankContact[];
  /** Files of the whole folder; filter by `work_order_id` for the current OS. */
  files: BankFile[];
  related_requests: BankRelatedRequest[];
}

/** Blocks the avaliahub-api stores; charges, files and related requests have no source yet. */
export type BankDossierBlocks = Omit<
  BankDossier,
  'charges' | 'files' | 'related_requests'
>;

export interface BankDossierResponse extends BankDossierBlocks {
  /** Control center id of the work order (not the reference number). */
  work_order_id: number;
  /** Set when the dossier was archived; the GET keeps returning it. */
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Text fields may go out empty-as-null; the server generates missing ids. */
type Nullable<T> = { [K in keyof T]: T[K] | null };

export type BankContactRequest = Nullable<Omit<BankContact, 'id'>> & {
  id?: string;
};

export type BankLinkedUnitRequest = Nullable<Omit<BankLinkedUnit, 'id'>> & {
  id?: string;
};

/**
 * Body of POST and PUT. An absent block is left as it is; a present block
 * replaces the stored one entirely (omitted fields become `null`, lists are
 * replaced). `appraisal_value: null` clears the value.
 */
export interface BankDossierRequest {
  identification?: BankIdentification;
  financing?: BankFinancing;
  appraisal_value?: number | null;
  milestones?: BankMilestones;
  appraisal_company?: BankAppraisalCompany;
  requesting_bank?: BankRequestingBank;
  address_extras?: BankAddressExtras;
  registry_extras?: Omit<BankRegistryExtras, 'linked_units'> & {
    linked_units: BankLinkedUnitRequest[];
  };
  characteristics_extras?: BankCharacteristicsExtras;
  report_kind?: BankReportKind;
  contacts?: BankContactRequest[];
}

export interface BankDossierEnvelope<T> {
  detail: {
    status: string;
    status_code: number;
    description?: string;
  };
  data?: T;
}
